import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    InputAdornment,
    CircularProgress,
    Alert,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Fade,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Lock as LockIcon,
    Person as PersonIcon,
    Visibility,
    VisibilityOff,
    Phone as PhoneIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import {
    signInWithPhoneNumber,
    RecaptchaVerifier,
    signOut,
    sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../../firebase/firebaseConfig';
import { loginUser } from './authSlice';
import { setNeedsOnboarding, setTrialEnd } from './onboardingSlice';
import { authenticatedFetch, publicFetch } from '../../utils/authApi';
import ReviewCarousel from './ReviewCarousel';
import MobileUnifiedLogin from './MobileUnifiedLogin';

const API_BASE_URL = import.meta.env.VITE_BACKEND_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:5001'
    : 'https://policytrackr-be-187769890360.asia-south1.run.app');

// Storage key for remembering last login mode
const LOGIN_MODE_KEY = 'policytrackr_login_mode';

/**
 * UnifiedLogin Component
 * 
 * A single polished login page with segmented toggle between Admin and Staff login.
 * - Desktop: Two-column layout with left branding/illustration and right login card
 * - Mobile: Full-screen with background image and centered login card overlay
 */
const UnifiedLogin = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Login mode: 'admin' or 'staff'
    const [loginMode, setLoginMode] = useState(() => {
        const savedMode = localStorage.getItem(LOGIN_MODE_KEY);
        return savedMode === 'staff' ? 'staff' : 'admin';
    });

    // General loading and auth check states
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [hasNavigated, setHasNavigated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Ref to track active OTP verification - prevents onAuthStateChanged from navigating prematurely
    const isVerifyingOtp = useRef(false);

    // Admin OTP states
    const [phoneNumber, setPhoneNumber] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [isPhoneValid, setIsPhoneValid] = useState(true);
    const [otp, setOtp] = useState('');
    const [otpStep, setOtpStep] = useState('phone'); // 'phone' or 'otp'
    const [confirmationResult, setConfirmationResult] = useState(null);

    // Staff login states
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [passwordError, setPasswordError] = useState('');
    const [isPasswordValid, setIsPasswordValid] = useState(true);

    // Forgot password states
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetSuccess, setResetSuccess] = useState(false);

    // Set page title for SEO and browser tab
    useEffect(() => {
        document.title = "Policytrackr - AI-powered virtual assistant for insurance agents";
    }, []);

    // Check if user is already logged in (only for page refresh, NOT during active OTP/login)
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            // Skip auto-navigation if we're in the middle of OTP verification
            // handleVerifyOtp will handle navigation after sync-admin-profile completes
            if (isVerifyingOtp.current) {
                setIsCheckingAuth(false);
                return;
            }
            if (user && !hasNavigated) {
                const isPhoneUser = user.providerData.some(p => p.providerId === 'phone');
                if (isPhoneUser) {
                    console.log('Phone user detected in UnifiedLogin, navigating to balance');
                    setHasNavigated(true);
                    navigate('/balance');
                    return;
                } else {
                    console.log('Email user detected in UnifiedLogin, navigating to renewals');
                    setHasNavigated(true);
                    navigate('/renewals');
                    return;
                }
            }
            setIsCheckingAuth(false);
        });

        return unsubscribe;
    }, [navigate, hasNavigated]);

    // Initialize reCAPTCHA when in admin mode
    useEffect(() => {
        if (loginMode === 'admin') {
            try {
                if (!window.recaptchaVerifier) {
                    window.recaptchaVerifier = new RecaptchaVerifier(
                        'recaptcha-container',
                        {
                            size: 'invisible',
                            callback: () => { },
                            'expired-callback': () => {
                                setError('Security verification expired. Please try again.');
                                if (window.recaptchaVerifier) {
                                    window.recaptchaVerifier.clear();
                                    window.recaptchaVerifier = null;
                                }
                            }
                        },
                        auth
                    );
                }
            } catch (err) {
                console.error('Error initializing reCAPTCHA:', err);
            }
        }

        return () => {
            if (window.recaptchaVerifier && loginMode !== 'admin') {
                try {
                    window.recaptchaVerifier.clear();
                } catch (err) {
                    console.error('Error clearing recaptcha:', err);
                }
                window.recaptchaVerifier = null;
            }
        };
    }, [loginMode]);

    // Handle login mode toggle
    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setLoginMode(newMode);
            localStorage.setItem(LOGIN_MODE_KEY, newMode);
            // Clear errors when switching
            setError('');
            setEmailError('');
            setPasswordError('');
            setPhoneError('');
            setIsEmailValid(true);
            setIsPasswordValid(true);
            setIsPhoneValid(true);
            setIsForgotPassword(false); // Reset forgot password view
            setResetSuccess(false);
            setResetEmail('');
            // Reset OTP step if switching away from admin
            if (newMode === 'staff') {
                setOtpStep('phone');
                setOtp('');
            }
        }
    };

    // ===== Phone Validation =====
    const validatePhone = (phone) => {
        const digitsOnly = phone.replace(/\D/g, '');
        return digitsOnly.length >= 10;
    };

    const handlePhoneChange = (e) => {
        // Allow numbers and initial '+' character
        const newPhone = e.target.value.replace(/[^\d+]/g, '').slice(0, 16);
        setPhoneNumber(newPhone);

        if (phoneError) {
            setPhoneError('');
            setIsPhoneValid(true);
        }
        if (error) setError('');
    };

    // ===== Email Validation =====
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (e) => {
        const newEmail = e.target.value;
        setEmail(newEmail);

        if (emailError) {
            setEmailError('');
            setIsEmailValid(true);
        }
        if (error) setError('');

        if (newEmail === '') {
            setEmailError('');
            setIsEmailValid(true);
        } else if (!validateEmail(newEmail)) {
            setEmailError('Please enter a valid email address');
            setIsEmailValid(false);
        } else {
            setEmailError('');
            setIsEmailValid(true);
        }
    };

    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);

        if (passwordError) {
            setPasswordError('');
            setIsPasswordValid(true);
        }
        if (error) setError('');
    };

    // ===== Staff Login Handler =====
    const handleStaffLogin = async (e) => {
        if (e) e.preventDefault();
        setError('');

        if (!isEmailValid || email === '') {
            setEmailError('Please enter a valid email address');
            setIsEmailValid(false);
            return;
        }

        if (!password || password.trim() === '') {
            setPasswordError('Please enter your password');
            setIsPasswordValid(false);
            return;
        }

        setLoading(true);
        try {
            const result = await dispatch(loginUser({ email, password }));
            if (result.meta.requestStatus === 'fulfilled') {
                navigate('/renewals');
            } else {
                throw result.error;
            }
        } catch (err) {
            if (err.code === 'auth/invalid-login-credentials') {
                setError('Incorrect email or password');
            } else if (err.code === 'auth/invalid-email') {
                setEmailError('Please enter a valid email address');
                setIsEmailValid(false);
            } else {
                setError(err.message || 'Login failed');
            }
        } finally {
            setLoading(false);
        }
    };

    // ===== Reset Password Handler =====
    const handleResetPassword = async (e) => {
        if (e) e.preventDefault();
        setError('');
        setResetSuccess(false);

        if (!resetEmail || !validateEmail(resetEmail)) {
            setError('Please enter a valid email address');
            return;
        }

        setLoading(true);
        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetSuccess(true);
            setError(''); // Clear any previous error on success
        } catch (err) {
            console.error('Error sending reset email:', err);
            if (err.code === 'auth/user-not-found') {
                setError('No user found with this email address');
            } else {
                setError(err.message || 'Failed to send reset email');
            }
        } finally {
            setLoading(false);
        }
    };

    // ===== Admin OTP Handlers =====
    const handleSendOtp = async (e) => {
        if (e) e.preventDefault();
        setError('');

        // Validate phone number - show error like staff login
        if (!phoneNumber || phoneNumber.trim() === '') {
            setPhoneError('Please enter your phone number');
            setIsPhoneValid(false);
            return;
        }

        if (!validatePhone(phoneNumber)) {
            setPhoneError('Please enter a valid 10-digit phone number');
            setIsPhoneValid(false);
            return;
        }

        setLoading(true);
        try {
            // Use existing + if present, otherwise default to +91
            let formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;

            // Pre-OTP Authorization Check
            const response = await publicFetch(`${API_BASE_URL}/api/check-admin-auth`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ phoneNumber: formattedPhone }),
            });

            if (!response.ok) {
                throw new Error('Failed to verify authorization. Please try again.');
            }

            const { authorized } = await response.json();

            if (!authorized) {
                setPhoneError('This phone number is not authorized for admin access.');
                setIsPhoneValid(false);
                setLoading(false);
                return;
            }

            if (!window.recaptchaVerifier) {
                try {
                    window.recaptchaVerifier = new RecaptchaVerifier(
                        'recaptcha-container',
                        { size: 'invisible' },
                        auth
                    );
                } catch (recaptchaError) {
                    console.error('Error re-creating RecaptchaVerifier:', recaptchaError);
                    setError('Failed to initialize security verification. Please refresh and try again.');
                    setLoading(false);
                    return;
                }
            }

            const result = await signInWithPhoneNumber(auth, formattedPhone, window.recaptchaVerifier);
            setConfirmationResult(result);
            setOtpStep('otp');
            setError('');
            setPhoneError('');
            setIsPhoneValid(true);
        } catch (err) {
            console.error('Error sending OTP:', err);
            if (err.code === 'auth/invalid-phone-number') {
                setPhoneError('Invalid phone number format.');
                setIsPhoneValid(false);
            } else if (err.code === 'auth/too-many-requests') {
                setError('Too many requests. Please try again later.');
            } else if (err.code === 'auth/captcha-check-failed') {
                setError('Security check failed. Please refresh and try again.');
            } else if (err.code === 'auth/invalid-app-credential') {
                setError('Localhost Login: Use a test phone number from Firebase Console.');
                console.error('⚠️ Firebase Auth: Real phone numbers are blocked on localhost.');
            } else {
                setError(err.message || 'Failed to send OTP. Please try again.');
            }

            if (err.code === 'auth/captcha-check-failed' && window.recaptchaVerifier) {
                try {
                    window.recaptchaVerifier.render().then(widgetId => {
                        window.recaptchaVerifier.reset(widgetId);
                    });
                } catch (resetErr) {
                    console.warn('Could not reset recaptcha', resetErr);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        if (e) e.preventDefault();
        setError('');

        if (!otp || otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }

        if (!confirmationResult) {
            setError('OTP session expired. Please start over.');
            return;
        }

        setLoading(true);
        isVerifyingOtp.current = true;
        try {
            console.log('🔐 [UnifiedLogin] Verifying OTP...');
            const userCredential = await confirmationResult.confirm(otp);
            const user = userCredential.user;
            console.log('✅ [UnifiedLogin] OTP verified successfully. UID:', user.uid);

            console.log('🔄 [UnifiedLogin] Calling sync-admin-profile...');
            const response = await authenticatedFetch(`${API_BASE_URL}/api/sync-admin-profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: user.uid,
                    phoneNumber: user.phoneNumber
                }),
            });

            if (!response.ok) {
                console.error('❌ [UnifiedLogin] Sync profile failed:', response.status);
                throw new Error('Verification failed on server. Please try again.');
            }

            const result = await response.json();
            console.log('✅ [UnifiedLogin] Profile synced result:', result);

            if (result.success) {
                // Check if admin needs onboarding
                if (!result.onboardingComplete) {
                    console.log('🆕 [UnifiedLogin] Admin needs onboarding, dispatching state...');
                    dispatch(setNeedsOnboarding(true));
                }
                if (result.trialEnd) {
                    dispatch(setTrialEnd(result.trialEnd));
                }

                console.log('🚀 [UnifiedLogin] Authorization confirmed. Navigating in 1.5s...');
                await new Promise(resolve => setTimeout(resolve, 1500));

                if (!auth.currentUser) {
                    console.error('❌ [UnifiedLogin] CRITICAL: User was signed out during wait!');
                }

                navigate('/balance');
            } else {
                console.warn('⚠️ [UnifiedLogin] Authorization failed in sync-admin-profile');
                await signOut(auth);
                setError('This phone number is not authorized for admin access.');
            }
        } catch (err) {
            console.error('❌ [UnifiedLogin] Error during OTP verification/sync:', err);
            if (err.code === 'auth/invalid-verification-code') {
                setError('Invalid OTP. Please check and try again.');
            } else if (err.code === 'auth/code-expired') {
                setError('OTP has expired. Please request a new one.');
                setOtpStep('phone');
                setOtp('');
            } else {
                setError(err.message || 'Failed to verify OTP.');
            }
        } finally {
            isVerifyingOtp.current = false;
            setLoading(false);
        }
    };

    // ===== Render Loading State =====
    if (isCheckingAuth) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    bgcolor: '#0051C3',
                }}
            >
                <CircularProgress sx={{ color: 'white' }} />
            </Box>
        );
    }

    if (isMobile) {
        return <MobileUnifiedLogin />;
    }

    // Fixed height for the form fields container to prevent any vertical movement
    const FORM_FIELDS_CONTAINER_HEIGHT = 200;

    // Common TextField styles
    const textFieldSx = {
        mb: 1, // Reduced from 2
        height: '70px', // Reduced from 80px
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            bgcolor: '#f8fafc',
            '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0051C3',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0051C3',
                borderWidth: '2px',
            }
        },
        '& .MuiFormHelperText-root': {
            margin: '2px 0 0 0',
            height: '14px', // Reduced from 20px
        }
    };

    // Common Button styles
    const secondaryButtonSx = {
        color: '#0051C3',
        fontWeight: 500,
        textTransform: 'none',
        fontSize: '0.85rem',
        height: '24px', // Reduced from 32px
        '&:hover': {
            textDecoration: 'underline',
            bgcolor: 'transparent',
        }
    };

    // ===== Main Render =====
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                position: 'relative',
                overflow: 'hidden',
                // Mobile: use background image
                ...(isMobile && {
                    backgroundImage: 'url(/login-illustration.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(0, 81, 195, 0.92) 0%, rgba(0, 51, 122, 0.95) 100%)',
                        zIndex: 1,
                    }
                })
            }}
        >
            {/* Left Section: Login Container on Blue Background */}
            <Box
                sx={{
                    flex: isMobile ? '1' : '0 0 50%',
                    background: 'linear-gradient(to bottom, #006AFF 0%, #003785 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    p: { xs: 2, sm: 4 },
                    zIndex: 2,
                }}
            >
                {/* Decorative background elements */}
                {!isMobile && (
                    <>
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '-10%',
                                left: '-10%',
                                width: '50%',
                                height: '50%',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                                borderRadius: '50%',
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: '-15%',
                                right: '-15%',
                                width: '60%',
                                height: '60%',
                                background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)',
                                borderRadius: '50%',
                            }}
                        />
                    </>
                )}

                <Fade in={true} timeout={600}>
                    <Paper
                        elevation={24}
                        sx={{
                            width: '100%',
                            maxWidth: { xs: '400px', sm: '420px' },
                            minHeight: 350, // Tightened further
                            borderRadius: '16px',
                            p: { xs: 3, sm: 4 },
                            bgcolor: 'white',
                            //boxShadow: '0 0 50px rgba(255, 255, 255, 0.4), 0 0 20px rgba(255, 255, 255, 0.2) !important',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            zIndex: 3
                        }}
                    >
                        {/* Title - "PolicyTrackr" in blue */}
                        <Box
                            sx={{
                                fontSize: { xs: '2rem', md: '2.8rem' },
                                fontWeight: 900,
                                lineHeight: 1,
                                fontFamily: '"Roboto Slab", serif !important',
                                letterSpacing: '-1.5px',
                                color: '#0051C3 !important',
                                textAlign: 'center',
                                mb: 2, // Reduced from 3 or 4
                                width: '100%'
                            }}
                        >
                            PolicyTrackr
                        </Box>


                        {/* Animated Pill Toggle */}
                        {!isForgotPassword && (
                            <Box
                                sx={{
                                    mb: 2,
                                    width: '100%',
                                    height: '44px',
                                    bgcolor: '#f1f5f9',
                                    borderRadius: '100px',
                                    p: '4px',
                                    display: 'flex',
                                    position: 'relative',
                                    cursor: 'pointer'
                                }}
                            >
                                {/* Animated Pill Background */}
                                <motion.div
                                    initial={false}
                                    animate={{ x: loginMode === 'admin' ? 0 : '100%' }}
                                    style={{
                                        position: 'absolute',
                                        top: '4px',
                                        left: '4px',
                                        width: 'calc(50% - 4px)',
                                        height: '36px',
                                        backgroundColor: 'white',
                                        borderRadius: '100px',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                                        zIndex: 0
                                    }}
                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                />

                                {/* Admin Option */}
                                <Box
                                    onClick={() => handleModeChange(null, 'admin')}
                                    sx={{
                                        flex: 1,
                                        zIndex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        color: loginMode === 'admin' ? '#0051C3' : '#64748b',
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        transition: 'color 0.2s',
                                        userSelect: 'none'
                                    }}
                                >
                                    <LockIcon sx={{ fontSize: 18 }} />
                                    Admin Login
                                </Box>

                                {/* Staff Option */}
                                <Box
                                    onClick={() => handleModeChange(null, 'staff')}
                                    sx={{
                                        flex: 1,
                                        zIndex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                        color: loginMode === 'staff' ? '#0051C3' : '#64748b',
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        transition: 'color 0.2s',
                                        userSelect: 'none'
                                    }}
                                >
                                    <PersonIcon sx={{ fontSize: 18 }} />
                                    Staff Login
                                </Box>
                            </Box>
                        )}

                        {/* Dynamic Title & Subtitle */}
                        <Box sx={{ textAlign: 'center', mb: 1 }}> {/* Reduced from 2 */}
                            <Fade in={true} key={isForgotPassword ? 'reset' : loginMode} timeout={300}>
                                <Box>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            color: '#1e293b',
                                            mb: 0.5,
                                            fontFamily: '"Roboto Slab", serif',
                                        }}
                                    >
                                        {isForgotPassword ? 'Reset Password' : ''}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        mb={1}
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '0.85rem', // Slightly smaller
                                        }}
                                    >
                                        {isForgotPassword
                                            ? 'Enter your registered email to receive a reset link.'
                                            : (loginMode === 'admin'
                                                ? 'Login with phone number & OTP'
                                                : 'Login with email & password')}
                                    </Typography>
                                </Box>
                            </Fade>
                        </Box>

                        <Box
                            component="form"
                            onSubmit={isForgotPassword
                                ? handleResetPassword
                                : (loginMode === 'admin'
                                    ? (otpStep === 'phone' ? handleSendOtp : handleVerifyOtp)
                                    : handleStaffLogin)
                            }
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <Box sx={{ position: 'relative', minHeight: '175px' }}> {/* Tightened further */}
                                <Fade in={true} key={isForgotPassword ? 'reset-form' : `form-${loginMode}`} timeout={300}>
                                    <Box>
                                        {isForgotPassword ? (
                                            <>
                                                <TextField
                                                    fullWidth
                                                    label="Email Address"
                                                    variant="outlined"
                                                    value={resetEmail}
                                                    onChange={(e) => {
                                                        setResetEmail(e.target.value);
                                                        if (error) setError('');
                                                    }}
                                                    disabled={loading}
                                                    error={!!error}
                                                    helperText={error || (resetSuccess ? 'Reset link sent! Check your email.' : '')}
                                                    FormHelperTextProps={{
                                                        sx: {
                                                            color: resetSuccess ? 'success.main' : 'error.main',
                                                            fontWeight: 500,
                                                            minHeight: '20px'
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <EmailIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={textFieldSx}
                                                />
                                                <Box display="flex" justifyContent="flex-end" sx={{ height: '24px', mt: 1 }}>
                                                    <Button
                                                        variant="text"
                                                        onClick={() => {
                                                            setIsForgotPassword(false);
                                                            setError('');
                                                            setResetSuccess(false);
                                                        }}
                                                        disabled={loading}
                                                        sx={secondaryButtonSx}
                                                    >
                                                        Back to Login
                                                    </Button>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                {/* Admin Login Fields */}
                                                {loginMode === 'admin' && (
                                                    <>
                                                        <TextField
                                                            fullWidth
                                                            label="Phone Number"
                                                            variant="outlined"
                                                            placeholder="10-digit number"
                                                            value={phoneNumber}
                                                            onChange={handlePhoneChange}
                                                            disabled={loading || otpStep === 'otp'}
                                                            error={!isPhoneValid}
                                                            helperText={phoneError}
                                                            inputProps={{
                                                                maxLength: 16,
                                                                inputMode: 'tel',
                                                                pattern: '[+0-9]*'
                                                            }}
                                                            FormHelperTextProps={{ sx: { minHeight: '20px' } }}
                                                            InputProps={{
                                                                startAdornment: !phoneNumber.startsWith('+') && (
                                                                    <InputAdornment position="start">
                                                                        <PhoneIcon sx={{ color: '#94a3b8', fontSize: 20, mr: 0.5 }} />
                                                                        +91
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            sx={textFieldSx}
                                                        />

                                                        {otpStep === 'otp' ? (
                                                            <TextField
                                                                fullWidth
                                                                label="OTP Code"
                                                                variant="outlined"
                                                                placeholder="Enter 6-digit OTP"
                                                                value={otp}
                                                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                                disabled={loading}
                                                                inputProps={{
                                                                    maxLength: 6,
                                                                    pattern: '[0-9]*',
                                                                    inputMode: 'numeric',
                                                                }}
                                                                sx={{ ...textFieldSx, mb: 1 }}
                                                            />
                                                        ) : (
                                                            <Box sx={{ height: '40px', mb: 1 }} /> // Reduced height from 80px
                                                        )}

                                                        <Box display="flex" justifyContent="flex-end" sx={{ height: '24px' }}> {/* Reduced from 32px */}
                                                            {otpStep === 'otp' && (
                                                                <Button
                                                                    variant="text"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setOtpStep('phone');
                                                                        setOtp('');
                                                                        setError('');
                                                                    }}
                                                                    disabled={loading}
                                                                    sx={secondaryButtonSx}
                                                                >
                                                                    Change phone number
                                                                </Button>
                                                            )}
                                                        </Box>
                                                    </>
                                                )}

                                                {/* Staff Login Fields */}
                                                {loginMode === 'staff' && (
                                                    <>
                                                        <TextField
                                                            fullWidth
                                                            label="Email"
                                                            variant="outlined"
                                                            value={email}
                                                            onChange={handleEmailChange}
                                                            error={!isEmailValid}
                                                            helperText={emailError}
                                                            disabled={loading}
                                                            FormHelperTextProps={{ sx: { minHeight: '10px' } }}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <EmailIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            sx={textFieldSx}
                                                        />

                                                        <TextField
                                                            fullWidth
                                                            label="Password"
                                                            variant="outlined"
                                                            type={showPassword ? 'text' : 'password'}
                                                            value={password}
                                                            onChange={handlePasswordChange}
                                                            error={!isPasswordValid}
                                                            helperText={passwordError}
                                                            disabled={loading}
                                                            FormHelperTextProps={{ sx: { minHeight: '14px', mb: 0 } }} // Reduced minHeight
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        <LockIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                                                    </InputAdornment>
                                                                ),
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            onClick={() => setShowPassword(!showPassword)}
                                                                            edge="end"
                                                                            size="small"
                                                                        >
                                                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            sx={{ ...textFieldSx, mb: 0.5 }} // Reduced mb
                                                        />

                                                        <Box display="flex" justifyContent="flex-end" sx={{ height: '20px' }}> {/* Reduced from 32px */}
                                                            <Button
                                                                variant="text"
                                                                onClick={() => {
                                                                    setIsForgotPassword(true);
                                                                    setError('');
                                                                    setResetEmail(email);
                                                                }}
                                                                sx={{ ...secondaryButtonSx, py: 0 }}
                                                            >
                                                                Forgot Password?
                                                            </Button>
                                                        </Box>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </Fade>
                            </Box>

                            {/* Error Alert */}
                            <Box sx={{ minHeight: (error && !isForgotPassword) ? '32px' : 0, mt: (error && !isForgotPassword) ? 0.5 : 0 }}> {/* Reduced minHeight and mt */}
                                {error && !isForgotPassword && (
                                    <Fade in={true}>
                                        <Alert
                                            severity="error"
                                            sx={{
                                                borderRadius: '10px',
                                                fontSize: '0.8rem',
                                                py: 0,
                                                '& .MuiAlert-icon': {
                                                    alignItems: 'center',
                                                    fontSize: '1.2rem'
                                                }
                                            }}
                                        >
                                            {error}
                                        </Alert>
                                    </Fade>
                                )}
                            </Box>

                            {/* Submit Button - MT AUTO to push to bottom of fixed container */}
                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    py: 0, // Reduced py
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #0051C3 0%, #003d91 100%)',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    boxShadow: '0 4px 14px rgba(0, 81, 195, 0.35)',
                                    transition: 'all 0.25s ease',
                                    mt: 0,
                                    '&:hover': {
                                        boxShadow: '0 6px 20px rgba(0, 81, 195, 0.45)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '&.Mui-disabled': {
                                        background: 'linear-gradient(135deg, #0051C3 0%, #003d91 100%)',
                                        color: 'white',
                                        opacity: 0.7,
                                    }
                                }}
                            >
                                {loading ? (
                                    <CircularProgress size={24} sx={{ color: 'white' }} />
                                ) : isForgotPassword ? (
                                    'SEND RESET LINK'
                                ) : loginMode === 'admin' ? (
                                    otpStep === 'phone' ? 'Send OTP' : 'Verify & Sign In'
                                ) : (
                                    'Login'
                                )}
                            </Button>
                        </Box>
                    </Paper>
                </Fade>
            </Box>

            {/* Right Section: Reviews Carousel on White Background */}
            {!isMobile && (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: '#f8fafc',
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <ReviewCarousel />
                </Box>
            )}

            {/* reCAPTCHA Container */}
            <div id="recaptcha-container" />
        </Box>
    );
};

export default UnifiedLogin;
